import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Space,
  Typography,
  message
} from 'antd';
import { SettingOutlined, SaveOutlined } from '@ant-design/icons';
import { useLanguage } from '../../contexts/LanguageContext';
import FieldDisplaySettingTable from '../FieldDisplaySettingTable';

const { Text } = Typography;

const EFormFieldDisplaySetting = ({
  visible,
  onClose,
  htmlContent,
  onSave,
  initialSettings = []
}) => {
  const { t } = useLanguage();
  const [fieldSettings, setFieldSettings] = useState([]);
  const [loading, setLoading] = useState(false);

  // 掃描 DOM 樹提取表單字段
  const scanFormFields = (html) => {
    if (!html) return [];

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const fields = [];

    const inputSelectors = [
      'input[type="text"]',
      'input[type="email"]',
      'input[type="tel"]',
      'input[type="number"]',
      'input[data-input-type="currency"]',
      'input[type="date"]',
      'input[data-input-type="date"]',
      'input[type="month"]',
      'input[type="time"]',
      'input[type="password"]',
      'input[type="hidden"]',
      'textarea',
      'select',
      'input[type="radio"]',
      'input[type="checkbox"]'
    ];

    inputSelectors.forEach((selector) => {
      const elements = doc.querySelectorAll(selector);
      elements.forEach((element, index) => {
        const fieldId = element.name || element.id || `${selector.replace(/[\[\]]/g, '')}_${index}`;
        const fieldType = element.tagName.toLowerCase();
        const dataInputType = element.getAttribute('data-input-type');
        const inputType = dataInputType === 'currency'
          ? 'currency'
          : (dataInputType === 'date' ? 'date' : (element.type || ''));
        const label = getFieldLabel(element);

        if (!fields.find((f) => f.fieldId === fieldId)) {
          fields.push({
            rowKey: fieldId,
            fieldId,
            fieldType,
            inputType,
            originalInputType: inputType, // 記錄原本 inputType，方便 JSON 模式關閉時還原
            originalLabel: label,
            displayLabel: label,
            showInList: true,
            order: fields.length
          });
        }
      });
    });

    return fields;
  };

  const getFieldLabel = (element) => {
    // 1. input 包在 <label> 裡
    const parentLabel = element.closest('label')?.textContent?.trim();
    if (parentLabel) return parentLabel;
    // 2. <label for="id"> 指向這個 input（兄弟節點模式）
    const elId = element.id || element.name;
    if (elId) {
      const doc = element.ownerDocument || document;
      const linkedLabel = doc.querySelector(`label[for="${elId}"]`)?.textContent?.trim();
      if (linkedLabel) return linkedLabel;
    }
    if (element.placeholder) return element.placeholder;
    if (element.name) return element.name;
    if (element.id) return element.id;
    return t('eformDesigner.fieldDisplaySetting.messages.unnamedField');
  };

  useEffect(() => {
    if (!visible || !htmlContent) return;
    const scannedFields = scanFormFields(htmlContent);

    if (initialSettings && initialSettings.length > 0) {
      // Merge：保留所有 initialSettings（包含使用者新增的 JSON 子列），補上掃描到但未存的新欄位
      const scannedById = new Map(scannedFields.map((f) => [f.fieldId, f]));
      const seenFieldIds = new Set();

      const merged = initialSettings.map((s) => {
        seenFieldIds.add(s.fieldId);
        const scanned = scannedById.get(s.fieldId);
        const isJson = (s.inputType || '') === 'json' || !!s.jsonKey;
        return {
          // 來自掃描的元資料（type/label）
          fieldType: scanned?.fieldType || s.fieldType || '',
          originalInputType: scanned?.inputType || s.originalInputType || s.inputType || '',
          originalLabel: scanned?.originalLabel || s.originalLabel || s.fieldId,
          // 使用者設定值
          fieldId: s.fieldId,
          inputType: s.inputType || scanned?.inputType || '',
          jsonKey: s.jsonKey || '',
          displayLabel: s.displayLabel || s.originalLabel || s.fieldId,
          showInList: !!s.showInList,
          order: typeof s.order === 'number' ? s.order : 0,
          rowKey: s.rowKey || (isJson && s.jsonKey ? `${s.fieldId}::${s.jsonKey}` : s.fieldId)
        };
      });

      // 新掃描到但 settings 沒有的欄位，預設不顯示
      scannedFields.forEach((f) => {
        if (!seenFieldIds.has(f.fieldId)) {
          merged.push({ ...f, showInList: false });
        }
      });

      // 依 order 穩定排序
      merged.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      merged.forEach((r, i) => { r.order = i; });

      setFieldSettings(merged);
    } else {
      setFieldSettings(
        scannedFields.map((field) => ({ ...field, showInList: false }))
      );
    }
  }, [visible, htmlContent, initialSettings]);

  const handleSave = async () => {
    setLoading(true);
    try {
      // 過濾掉 originalInputType（純前端用，不必落盤）
      const cleaned = fieldSettings.map(({ originalInputType, ...rest }) => rest);
      await onSave(cleaned);
      message.success(t('eformDesigner.fieldDisplaySetting.messages.saveSuccess'));
      onClose();
    } catch (error) {
      message.error(t('eformDesigner.fieldDisplaySetting.messages.saveFailed') + (error?.message || ''));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <SettingOutlined />
          <span>{t('eformDesigner.fieldDisplaySetting.modalTitle')}</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width="92%"
      style={{ top: 20 }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('eformDesigner.fieldDisplaySetting.buttons.cancel')}
        </Button>,
        <Button
          key="save"
          type="primary"
          icon={<SaveOutlined />}
          loading={loading}
          onClick={handleSave}
        >
          {t('eformDesigner.fieldDisplaySetting.buttons.confirm')}
        </Button>
      ]}
    >
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary">
          {t('eformDesigner.fieldDisplaySetting.helperTextNoJson')
            || t('eformDesigner.fieldDisplaySetting.helperText')}
        </Text>
      </div>

      <FieldDisplaySettingTable
        value={fieldSettings}
        onChange={setFieldSettings}
        showScreenColumn={false}
        supportJson={false}
      />
    </Modal>
  );
};

export default EFormFieldDisplaySetting;
